"use client";

import WorkflowItemCard, { IWorkflowItemCardProps } from 'src/components/WorkflowItemCard';
import React from 'react';
import { MainLayout, PageWithLayout } from '@shesha-io/reactjs';
import { createStyles } from 'antd-style';
import {
  InboxOutlined,
  ProfileOutlined,
  FileTextOutlined,
  SendOutlined,
} from '@ant-design/icons';

type WorkflowItem = Omit<IWorkflowItemCardProps, 'icon'> & {
  key: string;
  icon: React.ReactNode;
};

const WORKFLOW_ITEMS: WorkflowItem[] = [
  {
    key: 'my-items',
    title: 'My Items',
    description: 'Workflow items you own',
    icon: <ProfileOutlined />,
    accentColor: '#1677ff',
    entityType: 'Shesha.Workflow.Domain.WorkflowMyItem',
    specification: 'Shesha.Workflow.Specifications.MyItemOwnedByMeSpecification',
    route: '/dynamic/Shesha.Workflow/workflows-my-items',
  },
  {
    key: 'inbox-items',
    title: 'Inbox',
    description: 'Items assigned to you for action',
    icon: <InboxOutlined />,
    accentColor: '#722ed1',
    entityType: 'Shesha.Workflow.Domain.WorkflowInboxItem',
    specification: 'Shesha.Workflow.Specifications.InboxItemAssignedToMeSpecification',
    route: '/dynamic/Shesha.Workflow/workflows-inbox',
  },
  {
    key: 'draft-items',
    title: 'Drafts',
    description: 'Items you have started but not submitted',
    icon: <FileTextOutlined />,
    accentColor: '#fa8c16',
    entityType: 'Shesha.Workflow.Domain.WorkflowDraftItem',
    specification: 'Shesha.Workflow.Specifications.DraftItemCreatedByMeSpecification',
    route: '/dynamic/Shesha.Workflow/workflows-drafts',
  },
  {
    key: 'sent-items',
    title: 'Sent',
    description: 'Items you have completed and sent on',
    icon: <SendOutlined />,
    accentColor: '#52c41a',
    entityType: 'Shesha.Workflow.Domain.WorkflowSentItem',
    specification: 'Shesha.Workflow.Specifications.SentItemCompletedByMeSpecification',
    route: '/dynamic/Shesha.Workflow/workflows-sent',
  },
];

const useStyles = createStyles(({ token, css }) => ({
  page: css`
    min-height: 100%;
    padding: ${token.paddingXL}px;
    background-color: ${token.colorBgLayout};
    display: flex;
    justify-content: center;
  `,
  panel: css`
    width: 100%;
    max-width: 1280px;
    margin: 0 auto;
    padding: ${token.paddingXL}px;
    background-color: ${token.colorBgContainer};
    border: 1px solid ${token.colorBorderSecondary};
    border-radius: ${token.borderRadiusLG}px;
    box-shadow: ${token.boxShadowTertiary};
  `,
  heading: css`
    margin: 0 0 ${token.marginXXS}px;
    font-size: ${token.fontSizeHeading3}px;
    font-weight: 600;
    color: ${token.colorTextHeading};
  `,
  subheading: css`
    margin: 0 0 ${token.marginXL}px;
    font-size: ${token.fontSize}px;
    color: ${token.colorTextSecondary};
  `,
  grid: css`
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: ${token.marginLG}px;

    @media (max-width: 640px) {
      grid-template-columns: 1fr;
    }
  `,
}));

const Home: PageWithLayout<{}> = () => {
  const { styles } = useStyles();

  return (
    <MainLayout noPadding>
      <div className={styles.page}>
        <div className={styles.panel}>
          <h2 className={styles.heading}>My Workflow</h2>
          <p className={styles.subheading}>
            An overview of your workflow items. Select a card to view its list.
          </p>

          <div className={styles.grid}>
            {WORKFLOW_ITEMS.map(({ key, ...item }) => (
              <WorkflowItemCard key={key} {...item} />
            ))}
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default Home;
